import { Component, useCallback, useEffect, useState } from 'react';
import { RiDragMove2Line } from 'react-icons/ri';
import {
  useContentDispatch,
  useContentSelector,
  FlowsActions,
  CollectionComponetsActions,
  ComponentsActions,
} from '@wajeez-web/stores/content';
import {
  useCommonDispatch,
  AlertType,
  CommonActions,
} from '@wajeez-web/stores/common';
import { useParams, useNavigate } from 'react-router-dom';
import { ButtonHeadless, TextInput, Modal } from '@wajeez-web/components/ui';
import { Server } from '@wajeez/wajeez.microservices';
import { useDebounce } from '@wajeez-web/components/helpers';
import { useInfiniteScroll } from '@wajeez-web/components/page-view-builder';
import Skeleton from '@mui/material/Skeleton';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { AiOutlinePlus, AiOutlineEdit } from 'react-icons/ai';
// import NavigationPrompt from 'react-router-navigation-prompt';
import { BiLoaderAlt } from 'react-icons/bi';
import ReactRouterPrompt from 'react-router-prompt';

import { Sortable, SortableItem } from '../components/Sortable/Sortable';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { Dropdown } from '@wajeez-web/components/mui';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { CloneModal } from '../components/CloneModal';

const pageNames = [
  'home',
  'explore',
  'searchAll',
  'b2BHome',
  'b2BLibrary',
  'b2BSkills',
];

type PageOrNavigation = {
  pageId: string | undefined;
  pageName: string | undefined;
  pageType: string | undefined;
  navigationId?: number | undefined;
};

const PAGE_SIZE = 10;

const FlowsContentPage = () => {
  const { flowId, pageId } = useParams<{ flowId: string; pageId: string }>();

  const navigate = useNavigate();

  const contentDispatch = useContentDispatch();
  const commonDispatch = useCommonDispatch();

  const [selectedPage, setSelectedPage] = useState<
    PageOrNavigation | undefined
  >();
  const [pages, setPages] = useState<PageOrNavigation[]>([]);
  const [navigations, setNavigations] = useState<PageOrNavigation[]>();
  const [mappedAllowedComponents, setMappedAllowedComponents] = useState<
    (Server.ComponentOutput & { isDisabled: boolean })[]
  >([]);
  const [addedComponentsOriginal, setAddedComponentsOriginal] = useState<
    { id: string | undefined; referenceName: string | undefined }[] | undefined
  >([]);
  const [addedComponents, setAddedComponents] = useState<
    { id: string | undefined; referenceName: string | undefined }[] | undefined
  >([]);
  const [allowedComponentsPageNumber, setAllowedComponentsPageNumber] =
    useState(1);
  const [allowedComponentsSearchTerm, setAllowedComponentsSearchTerm] =
    useState<string | undefined>('');
  const [shouldAddedComponentsUpdate, setShouldAddedComponentsUpdate] =
    useState(false);
  const [canNavigate, setCanNavigate] = useState(true);
  const [navigationPageId, setNavigationPageId] = useState<string>();
  const [isFlowDetailsSuccess, setIsFlowDetailsSuccess] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string>();
  const [isCloneComponentModalOpen, setIsCloneComponentModalOpen] =
    useState(false);
  const [isCloneComponentSaveSuccess, setIsCloneComponentSaveSuccess] =
    useState(false);

  const flowDetails = useContentSelector(({ flows }) => flows.flowDetailsById);
  const flowDetailsData = flowDetails.data;

  const collectionComponentDetails = useContentSelector(
    ({ collectionComponents }) => collectionComponents.details
  );
  const collectionComponentDetailsStatus = useContentSelector(
    ({ collectionComponents }) => collectionComponents.detailsStatus
  );
  const collectionComponentAddStatus = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.addComponentToCollectionComponent.status
  );
  const collectionComponentRemoveStatus = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.removeComponentFromCollectionComponent.status
  );
  const setCollectionComponentDynamicZoneStatus = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.setCollectionComponentDynamicZone.status
  );

  const allowedComponents = useContentSelector(
    ({ components }) => components.allowedComponentsForCollectionComponentType
  );
  const allowedComponentsData = allowedComponents.data;

  const cloneComponent = useContentSelector(
    ({ components }) => components.cloneComponent
  );

  const { lastElementRef } = useInfiniteScroll(
    allowedComponents.meta.hasNext,
    setAllowedComponentsPageNumber
  );

  const [setSearchTermDebounced] = useDebounce((data) => {
    contentDispatch(
      ComponentsActions.clearAllowedComponentsForCollectionComponentType()
    );

    setAllowedComponentsSearchTerm(data?.value);

    setAllowedComponentsPageNumber(1);
  });

  useEffect(() => {
    if (selectedPage) {
      navigate(`/flows/${flowId}/pages/${selectedPage.pageId}`);
    }
  }, [pages, flowId, selectedPage]);

  useEffect(() => {
    const addedComponentsDataMapped =
      collectionComponentDetails?.dynamicZone?.map((d) => {
        return {
          id: d.id,
          referenceName: d.referenceName,
        };
      });

    setAddedComponents(addedComponentsDataMapped);

    setAddedComponentsOriginal(addedComponentsDataMapped);
  }, [collectionComponentDetails]);

  useEffect(() => {
    setIsFlowDetailsSuccess(false);

    (async function () {
      const res = await contentDispatch(
        FlowsActions.getFlowDetailsByIdAsync(Number(flowId))
      );

      if (res.meta.requestStatus === 'fulfilled') {
        setIsFlowDetailsSuccess(true);
      }
    })();
  }, [flowId]);

  useEffect(() => {
    const pages = pageNames.map((p) => {
      return {
        pageId: flowDetailsData?.[
          `${p}PageId` as keyof Server.FlowOutput
        ] as string,
        pageName: flowDetailsData?.[
          `${p}PageName` as keyof Server.FlowOutput
        ] as string,
        pageType: flowDetailsData?.[
          `${p}PageType` as keyof Server.FlowOutput
        ] as string,
      };
    });

    if (isFlowDetailsSuccess) {
      setPages(pages.filter((p) => p.pageName));

      setNavigations(
        flowDetailsData?.typeNavigations
          ?.filter((n) => n.detailsPageId)
          .map((n) => {
            return {
              pageId: n.detailsPageId,
              pageName: n.contentEntityType,
              pageType: n.detailsPageType,
              navigationId: n.id,
            };
          })
      );
    }
  }, [flowDetailsData, isFlowDetailsSuccess]);

  const onPageChange = useCallback(
    (id: string | undefined) => {
      if (isFlowDetailsSuccess) {
        const foundPage = pages
          ?.concat(navigations || [])
          .find((p) => p.pageId === id);

        if (foundPage) {
          setSelectedPage(foundPage);
        } else {
          setSelectedPage(pages?.[0]);
        }
      }
    },
    [pages, navigations, isFlowDetailsSuccess]
  );

  useEffect(() => {
    onPageChange(pageId);
  }, [pageId, onPageChange]);

  useEffect(() => {
    selectedPage?.pageId &&
      contentDispatch(
        CollectionComponetsActions.getCollectionComponentByIdAsync(
          selectedPage?.pageId
        )
      );
  }, [selectedPage]);

  useEffect(() => {
    return () => {
      contentDispatch(
        ComponentsActions.clearAllowedComponentsForCollectionComponentType()
      );

      contentDispatch(
        CollectionComponetsActions.clearCollectionComponentsById()
      );
    };
  }, []);

  useEffect(() => {
    selectedPage?.pageType &&
      contentDispatch(
        ComponentsActions.getAllowedComponentsForCollectionComponentTypeAsync({
          pageNumber: allowedComponentsPageNumber,
          pageSize: PAGE_SIZE,
          collectionComponentTypeName: selectedPage.pageType,
          searchText: allowedComponentsSearchTerm,
        })
      );
  }, [selectedPage, allowedComponentsPageNumber, allowedComponentsSearchTerm]);

  useEffect(() => {
    setMappedAllowedComponents(
      allowedComponentsData.map((c, i) => {
        const foundAddedComponent =
          collectionComponentDetails?.dynamicZone?.find((d) => d.id === c.id);

        if (foundAddedComponent) {
          return {
            ...c,
            isDisabled: true,
          };
        } else {
          return {
            ...c,
            isDisabled: false,
          };
        }
      })
    );
  }, [allowedComponentsData, collectionComponentDetails]);

  useEffect(() => {
    let shouldUpdate = false;

    if (addedComponents) {
      for (const i in addedComponents) {
        if (addedComponents[i].id !== addedComponentsOriginal?.[i]?.id) {
          shouldUpdate = true;

          break;
        }
      }

      if (addedComponentsOriginal?.length && !addedComponents.length) {
        shouldUpdate = true;
      }

      if (
        addedComponentsOriginal &&
        addedComponents.length < addedComponentsOriginal.length
      ) {
        shouldUpdate = true;
      }
    }

    setShouldAddedComponentsUpdate(shouldUpdate);
  }, [addedComponents, addedComponentsOriginal]);

  const onItemsUpdate = (args: { successMessage?: string }) => {
    contentDispatch(CollectionComponetsActions.clearCollectionComponentsById());

    selectedPage?.pageId &&
      contentDispatch(
        CollectionComponetsActions.getCollectionComponentByIdAsync(
          selectedPage.pageId
        )
      );

    contentDispatch(
      ComponentsActions.getAllowedComponentsForCollectionComponentTypeAsync({
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        collectionComponentTypeName: selectedPage?.pageType,
        searchText: '',
      })
    );

    args.successMessage &&
      commonDispatch(
        CommonActions.setAlert({
          for: 'page',
          show: true,
          type: AlertType.SUCCESS,
          messages: [
            {
              code: 200,
              field: null,
              message: args.successMessage,
            },
          ],
        })
      );
  };

  useEffect(() => {
    if (isCloneComponentSaveSuccess && cloneComponent.data) {
      navigate(
        `/flows/${flowId}/pages/${pageId}/components/${cloneComponent.data}/edit`
      );

      setIsCloneComponentSaveSuccess(false);
      setIsCloneComponentModalOpen(false);
    }
  }, [cloneComponent.data, isCloneComponentSaveSuccess]);

  const renderPages = () => {
    return pages?.map((p) => {
      return (
        <div
          key={`${p.pageId}-${p.pageName}`}
          className={`py-2 px-2 font-medium hover:bg-warmGray-100 hover:text-primary rounded-[4px] transition-colors cursor-pointer ${
            selectedPage?.pageId === p.pageId && 'bg-warmGray-100 text-primary'
          }`}
          onClick={() => {
            if (shouldAddedComponentsUpdate) {
              setCanNavigate(false);

              setNavigationPageId(p.pageId);

              return;
            }

            if (p.pageId !== selectedPage?.pageId) {
              contentDispatch(
                CollectionComponetsActions.clearCollectionComponentsById()
              );

              contentDispatch(
                ComponentsActions.clearAllowedComponentsForCollectionComponentType()
              );

              setSelectedPage(p);

              navigate(`/flows/${flowId}/pages/${p.pageId}`);
            }
          }}
        >
          {p.pageName}
        </div>
      );
    });
  };

  const renderNavigations = () => {
    return navigations?.map((n) => {
      return (
        <div
          key={`${n.pageId}-${n.pageName}`}
          className={`py-2 px-2 font-medium hover:bg-warmGray-100 hover:text-primary rounded-[4px] transition-colors cursor-pointer ${
            selectedPage?.navigationId === n.navigationId &&
            'bg-warmGray-100 text-primary'
          }`}
          onClick={() => {
            if (shouldAddedComponentsUpdate) {
              setCanNavigate(false);

              setNavigationPageId(n.pageId);

              return;
            }

            if (n.navigationId !== selectedPage?.navigationId) {
              contentDispatch(
                CollectionComponetsActions.clearCollectionComponentsById()
              );

              contentDispatch(
                ComponentsActions.clearAllowedComponentsForCollectionComponentType()
              );

              setSelectedPage(n);

              navigate(`/flows/${flowId}/pages/${n.pageId}`);
            }
          }}
        >
          {n.pageName}
        </div>
      );
    });
  };

  const renderComponentsSkeleton = () => {
    return Array.from({ length: 10 }).map((_, i) => {
      return {
        id: i.toString(),
        label: (
          <div className="h-full mt-3">
            <Skeleton
              key={i}
              sx={{ bgcolor: '#ededed' }}
              variant="rounded"
              width={'100%'}
              height={'100%'}
            />
          </div>
        ),
        isDisabled: false,
      };
    });
  };

  const renderAddedComponents = () => {
    const data = addedComponents?.map((d) => {
      return {
        id: d.id!,
        label: (
          <div key={d.id} className="w-full h-full">
            <div className="w-[70%] mx-auto h-full flex justify-center items-center text-[18px] text-center">
              {d.referenceName}
            </div>
          </div>
        ),
        actionsNode: (
          <div>
            <Dropdown
              id={`component-actions-${d.id}`}
              items={[
                {
                  label: 'Edit',
                  onClick: () => {
                    navigate(
                      `/flows/${flowId}/pages/${pageId}/components/${d.id}/edit`
                    );
                  },
                },
                {
                  label: 'Clone',
                  onClick: () => {
                    setSelectedComponentId(d.id);
                    setIsCloneComponentModalOpen(true);
                  },
                },
              ]}
              label={
                <div style={{ color: '#343a40' }}>
                  <DotsHorizontalIcon
                    style={{ width: '28px', height: '24px' }}
                  />
                </div>
              }
              buttonStyle={{
                width: '34px',
                minWidth: '34px',
                paddingRight: '2px',
                paddingLeft: '2px',
                color: 'black',
              }}
            />
          </div>
        ),
      };
    });

    if (
      (collectionComponentDetailsStatus === 'loading' &&
        !collectionComponentDetails) ||
      collectionComponentRemoveStatus === 'loading'
    ) {
      return renderComponentsSkeleton();
    } else {
      return data;
    }
  };

  const renderComponents = () => {
    const data: SortableItem[] = mappedAllowedComponents.map((c, i) => {
      return {
        id: c.id!,
        label: (
          <div
            key={c.id}
            ref={
              i === mappedAllowedComponents.length - 1 ? lastElementRef : null
            }
            className="w-full h-full"
          >
            <div className="w-[70%] mx-auto h-full flex justify-center items-center text-[18px] text-center">
              {c.referenceName}
            </div>
          </div>
        ),
        isDisabled: c.isDisabled,
        actionsNode: (
          <div>
            <Dropdown
              id={`component-actions-${c.id}`}
              items={[
                {
                  label: 'Edit',
                  onClick: () => {
                    navigate(
                      `/flows/${flowId}/pages/${pageId}/components/${c.id}/edit`
                    );
                  },
                },
                {
                  label: 'Clone',
                  onClick: () => {
                    setSelectedComponentId(c.id);
                    setIsCloneComponentModalOpen(true);
                  },
                },
              ]}
              label={
                <div style={{ color: '#343a40' }}>
                  <DotsHorizontalIcon
                    style={{ width: '28px', height: '24px' }}
                  />
                </div>
              }
              buttonStyle={{
                width: '34px',
                minWidth: '34px',
                paddingRight: '2px',
                paddingLeft: '2px',
                color: 'black',
              }}
            />
          </div>
        ),
      };
    });

    if (
      allowedComponents.status === 'loading' &&
      allowedComponentsData.length
    ) {
      return data.concat([
        {
          id: 'spinner',
          label: (
            <div className="py-3 w-full flex justify-center items-center">
              <BiLoaderAlt className="animate-spin text-[24px]" />
            </div>
          ),
          isDisabled: false,
          actionsNode: null,
        },
      ]);
    } else if (
      (allowedComponents.status === 'loading' &&
        !allowedComponentsData.length) ||
      collectionComponentAddStatus === 'loading'
    ) {
      return renderComponentsSkeleton();
    } else {
      return data;
    }
  };

  return (
    <div className="w-full h-inner-screen flex justify-start items-start">
      <div className="w-[300px] h-full px-4 py-6 border-e border-warmGray-200 overflow-y-auto overflow-x-hidden">
        <h1 className="w-full pb-2 border-b text-[20px] font-bold text-center break-words">
          {flowDetailsData?.flowName}
        </h1>
        <div className="mt-6">
          <div className="px-2 text-[14px] text-warmGray-400 font-medium">
            Pages
          </div>
          <div className="mt-3">{renderPages()}</div>
        </div>
        <div className="mt-6">
          <div className="px-2 text-[14px] text-warmGray-400 font-medium">
            Navigations
          </div>
          <div className="mt-3">{renderNavigations()}</div>
        </div>
      </div>
      <div className="w-full h-full py-6 px-8">
        <div className="w-full flex justify-between items-center">
          <div className="flex justify-start items-center space-s-2">
            <div
              className="text-[19px] text-primary cursor-pointer"
              onClick={() => {
                navigate(`/flows/${flowId}/pages/${pageId}/edit`);
              }}
            >
              <AiOutlineEdit />
            </div>
            <h1 className="mb-0 text-[26px] font-bold">
              {selectedPage?.pageName}
            </h1>
          </div>
          <div className="w-[70px]">
            <ButtonHeadless
              paddingYStyle="py-1"
              action={async () => {
                const res = await contentDispatch(
                  CollectionComponetsActions.setCollectionComponentDynamicZoneAsync(
                    {
                      dynamicZoneContainerId:
                        collectionComponentDetails?.id || '',
                      componentIds: addedComponents
                        ?.map((c) => c.id)
                        .filter((id) => id) as string[],
                    }
                  )
                );

                if (res.meta.requestStatus === 'fulfilled') {
                  commonDispatch(
                    CommonActions.setAlert({
                      for: 'page',
                      show: true,
                      type: AlertType.SUCCESS,
                      messages: [
                        {
                          code: 200,
                          field: null,
                          message:
                            'The components have been updated successfully!',
                        },
                      ],
                    })
                  );

                  setAddedComponentsOriginal(addedComponents);

                  setShouldAddedComponentsUpdate(false);
                } else {
                  onItemsUpdate({});
                }
              }}
              isDisabled={!shouldAddedComponentsUpdate}
              bgStyle={
                shouldAddedComponentsUpdate ? 'bg-primary' : 'bg-[#c2c2c2]'
              }
              borderRadiusStyle="rounded"
            >
              <div className="w-full flex justify-center items-center text-white font-bold text-[16px]">
                <ReturnTranslatedText
                  origintext="Save"
                  text={`buttons.${'Save'}`}
                />
                {setCollectionComponentDynamicZoneStatus === 'loading' && (
                  <BiLoaderAlt className="mr-1 animate-spin w-5" />
                )}
              </div>
            </ButtonHeadless>
          </div>
        </div>
        <div className="mt-6">
          <Sortable
            data={[renderAddedComponents() || [], renderComponents()]}
            containerClassName="w-1/2 h-[70vh] px-8 py-6 border rounded-md overflow-y-auto overflow-x-hidden"
            containerHeaders={{
              firstContainer: (
                <h2 className="text-[20px] font-bold text-center">
                  <ReturnTranslatedText
                    origintext="Added Components"
                    text={`general.${'Added Components'}`}
                  />
                </h2>
              ),
              secondContainer: (
                <>
                  <div className="relative w-full">
                    <h2 className="text-[20px] font-bold text-center">
                      <ReturnTranslatedText
                        origintext="Components"
                        text={`general.${'Components'}`}
                      />
                    </h2>
                    <div className="absolute -top-1 left-2 w-[35px]">
                      <ButtonHeadless
                        paddingYStyle="py-[2px]"
                        action={() => {
                          navigate(
                            `/flows/${flowId}/pages/${selectedPage?.pageId}/components/new`
                          );
                        }}
                        borderRadiusStyle="rounded"
                        bgStyle="bg-white"
                        borderStyle="border border-primary"
                        opacityOnHover={false}
                      >
                        <div className="text-primary text-[20px]">
                          <AiOutlinePlus />
                        </div>
                      </ButtonHeadless>
                    </div>
                  </div>
                  <TextInput
                    name="component-search"
                    label="Search"
                    onChange={(name, value) => {
                      setSearchTermDebounced({ value });
                    }}
                  />
                </>
              ),
            }}
            hasExtraDeleteContainer={true}
            onItemAddToFirstContainer={async (id, order) => {
              setMappedAllowedComponents(
                mappedAllowedComponents.map((ac) => {
                  if (ac.id === id) {
                    return {
                      ...ac,
                      isDisabled: true,
                    };
                  } else {
                    return ac;
                  }
                })
              );

              const addedComponent = mappedAllowedComponents.find(
                (ac) => ac.id === id
              );

              const addedComponentsFirstSlice = addedComponents?.slice(
                0,
                order
              );
              const addedComponentsSecondSlice = addedComponents?.slice(order);

              setAddedComponents(
                addedComponentsFirstSlice?.concat(
                  {
                    id: addedComponent?.id,
                    referenceName: addedComponent?.referenceName,
                  },
                  addedComponentsSecondSlice || []
                )
              );
            }}
            onItemDeleteFromFirstContainer={async (id) => {
              setMappedAllowedComponents(
                mappedAllowedComponents.map((ac) => {
                  if (ac.id === id) {
                    return {
                      ...ac,
                      isDisabled: false,
                    };
                  } else {
                    return ac;
                  }
                })
              );

              setAddedComponents(addedComponents?.filter((c) => c.id !== id));
            }}
            onFirstContainerReorder={async (ids) => {
              setAddedComponents(
                ids.map((id) => {
                  return {
                    id,
                    referenceName: addedComponents?.find((c) => c.id === id)
                      ?.referenceName,
                  };
                })
              );
            }}
            onSecondContainerReorder={async () => {
              setMappedAllowedComponents(mappedAllowedComponents);
            }}
          />
        </div>
      </div>
      <ReactRouterPrompt when={shouldAddedComponentsUpdate}>
        {(props) => {
          const { isActive, onConfirm, onCancel } = props;

          return (
            <ConfirmationModal
              isOpen={isActive}
              onClose={onCancel}
              onSubmit={onConfirm}
              message="Are you sure that you want to leave this page?"
            />
          );
        }}
      </ReactRouterPrompt>
      {!canNavigate && (
        <ConfirmationModal
          isOpen={!canNavigate}
          onClose={() => {
            setCanNavigate(true);
          }}
          onSubmit={() => {
            setCanNavigate(true);

            if (navigationPageId !== selectedPage?.pageId) {
              contentDispatch(
                CollectionComponetsActions.clearCollectionComponentsById()
              );

              contentDispatch(
                ComponentsActions.clearAllowedComponentsForCollectionComponentType()
              );

              onPageChange(navigationPageId);

              navigate(`/flows/${flowId}/pages/${navigationPageId}`);
            }
          }}
          message="Are you sure that you want to leave this page?"
        />
      )}
      {isCloneComponentModalOpen && (
        <CloneModal
          title="Clone Component"
          isOpen={isCloneComponentModalOpen}
          onClose={() => {
            setIsCloneComponentModalOpen(false);
          }}
          onSave={async (name) => {
            const res = await contentDispatch(
              ComponentsActions.cloneComponentAsync({
                referenceName: name,
                id: selectedComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsCloneComponentModalOpen(false);
            }
          }}
          onSaveAndEdit={async (name) => {
            const res = await contentDispatch(
              ComponentsActions.cloneComponentAsync({
                referenceName: name,
                id: selectedComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsCloneComponentSaveSuccess(true);
            }
          }}
          isLoading={cloneComponent.status === 'loading'}
        />
      )}
    </div>
  );
};

export default FlowsContentPage;
